import { FE_UPLT_HISTORY_TIMING_V2_250401 } from "@faire/retailer-settings/FE_UPLT_HISTORY_TIMING_V2_250401";
import { PageInitialized } from "@faire/retailer-visitor-shared/events";
import { Category } from "@faire/retailer-visitor-shared/models/Category/Category";
import { PageLoadEvent } from "@faire/retailer-visitor-shared/services/performance/const";
import { PerformanceMarker } from "@faire/retailer-visitor-shared/services/performance/PerformanceMarker";
import { useAssignEffect } from "@faire/retailer-visitor-shared/settings/useEffectAssignWhen";
import { useFrontendSetting } from "@faire/retailer-visitor-shared/settings/useFrontendSetting";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { LoggedOutCategoryPageFilterStoreProvider } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/LoggedOutCategoryPageFilterStoreProvider";
import {
  LoggedOutCategoryPageCurrentCategoryProvider,
  useLoggedOutCategoryPageCurrentCategory,
} from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { LoggedOutCategoryPageCurrentNavigationPathProvider } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentNavigationPath";
import { useLoggedOutCategoryPageCatNavResponse } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageCatNav/useLoggedOutCategoryPageCatNavResponse";
import { useLoggedOutCategoryPageFilterSections } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/DataFetching/useLoggedOutCategoryPageCatNav/useLoggedOutCategoryPageFilterSections";
import { useLoggedOutCategoryPageisLoadedAndHasNoResults } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/hooks/useLoggedOutCategoryPageisLoadedAndHasNoResults";
import { LoggedOutCategoryPageBase } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/LoggedOutCategoryPageBase";
import { trackRetailersPageViewView } from "@faire/web-api--source/events/retailers/view/pageView";
import { HAS_VISITED_CATEGORY_SEARCH_PAGES } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/HAS_VISITED_CATEGORY_SEARCH_PAGES";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import React, { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

const InternalLoggedOutCategoryPage: React.FC = () => {
  const category = useLoggedOutCategoryPageCurrentCategory();

  const {
    isFetching: isCatNavFetching,
    dataUpdatedAt,
    error,
    request: catNavRequest,
  } = useLoggedOutCategoryPageCatNavResponse();

  const { isFetching: isFiltersFetching, request: filtersRequest } =
    useLoggedOutCategoryPageFilterSections();

  const isLoading = isCatNavFetching || isFiltersFetching;
  const isLoadedAndHasNoResults =
    useLoggedOutCategoryPageisLoadedAndHasNoResults();

  const { setHasOccurred: setHasVisitedCategorySearchPages } = useMilestone(
    HAS_VISITED_CATEGORY_SEARCH_PAGES
  );

  useEffect(() => {
    PerformanceMarker.get().mark(PageLoadEvent.pageStart);
  }, []);

  useEffect(() => {
    setHasVisitedCategorySearchPages();
  }, [setHasVisitedCategorySearchPages]);

  useLayoutEffect(() => {
    if (!isLoading) {
      PageInitialized.publish();
    }
  }, [isLoading]);

  useAssignEffect(FE_UPLT_HISTORY_TIMING_V2_250401, true);
  const isSettingHistoryTimingV2 = useFrontendSetting(
    FE_UPLT_HISTORY_TIMING_V2_250401
  );

  const pageName = "page-category-product-logged-out";
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: pageName,
    pageLoadKey: JSON.stringify({ catNavRequest, filtersRequest }),
    dataUpdatedAt,
    numComponents: 1,
    ignoreImages: isLoadedAndHasNoResults || !!error,
    isPageLoading: isLoading,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
    enablePageUpdateFix: isSettingHistoryTimingV2 ? "v2" : undefined,
  });

  const { pathname } = useLocation();
  const pageType = pathname.includes("/suppliers") ? "suppliers" : "category";
  const [c1, c2, c3] = category.urlIdStrings;

  useEffect(() => {
    trackRetailersPageViewView(
      "",
      "",
      "",
      pageType,
      pathname,
      pageName,
      c1 ?? "",
      c2 ?? "",
      c3 ?? ""
    );
  }, [pageType, pathname, pageName, c1, c2, c3]);

  return (
    <LoggedOutCategoryPageBase
      categoryPageLoadTimingRef={isLoading ? undefined : loadTimingRef}
    />
  );
};

export const LoggedOutCategoryPage: React.FC<{
  category: Category;
  navigationPath: string;
}> = ({ category, navigationPath }) => {
  return (
    <LoggedOutCategoryPageCurrentCategoryProvider value={category}>
      <LoggedOutCategoryPageCurrentNavigationPathProvider
        value={navigationPath}
      >
        <LoggedOutCategoryPageFilterStoreProvider>
          <InternalLoggedOutCategoryPage />
        </LoggedOutCategoryPageFilterStoreProvider>
      </LoggedOutCategoryPageCurrentNavigationPathProvider>
    </LoggedOutCategoryPageCurrentCategoryProvider>
  );
};
